/* eslint-disable react-hooks/exhaustive-deps */
import React, { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';

import SwitchCase from 'components/Common/SwitchCase';
import { Seo } from 'shared/seo';

import CompletePage from './complete';
import RegisterInfoPage from './registerInfo';

function RegisterPage() {
  // 가입은 항상 정보 입력(depth 0)부터 시작한다.
  // (과거 `?step=` 점프 파라미터는 가입 코드 단계 제거로 depth가 0/1로 당겨지면서
  //  의미가 깨졌다 — step=1이 완료 화면으로, step=2가 빈 화면으로 빠지므로 제거했다.)
  const [depth, setDepth] = useState<RegisterDepthContextType['depth']>(0);

  return (
    <>
      <Seo
        title="회원가입 | 체크메이트코리아"
        description="체크메이트코리아 부동산·숙박업소 회원가입 페이지입니다. 매물을 등록하고 외국인 고객과 비대면으로 가계약을 체결해보세요."
        path="/register"
      />
      <RegisterDepthProvider depth={depth} setDepth={setDepth}>
        <SwitchCase
          value={depth}
          caseBy={{
            0: <RegisterInfoPage />,
            1: <CompletePage />,
          }}
        />
      </RegisterDepthProvider>
    </>
  );
}

export default RegisterPage;

type RegisterDepthContextType = {
  depth: 0 | 1;
  nextPage: () => void;
};
const RegisterDepthContext = createContext<RegisterDepthContextType>({
  depth: 0,
  nextPage: () => null,
});

const RegisterDepthProvider = ({
  children,
  depth,
  setDepth,
}: PropsWithChildren<{
  depth: RegisterDepthContextType['depth'];
  setDepth: React.Dispatch<React.SetStateAction<RegisterDepthContextType['depth']>>;
}>) => {
  const nextPage = () => {
    setDepth((prev) => Math.min(prev + 1, 1) as RegisterDepthContextType['depth']);
  };
  const value = {
    depth,
    nextPage,
  };

  useEffect(() => {
    const handlePopState = () => setDepth((prev) => Math.max(0, prev - 1) as RegisterDepthContextType['depth']);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  return <RegisterDepthContext.Provider value={value}>{children}</RegisterDepthContext.Provider>;
};

export const useRegisterNextPage = () => {
  const { nextPage } = useContext(RegisterDepthContext);
  return nextPage;
};
